import { FilterMatchMode } from "primereact/api";
import {
  defaultPagination,
  SortDirection,
} from "../../../../supportFunctions/graphql/Queries";
import { useTranslation } from "react-i18next";
import { Dispatch, useState, useEffect } from "react";
import {
  Card,
  Table,
  AdvancedFilterColumn,
  Toggle,
} from "@velocity-ehs/component-library";
import { Column } from "primereact/column";
import { useGetEhsPermissions } from "../../../../../hooks/useGetEhsPermissions";
import { useGetEhsCapabilities } from "../../../../../hooks/useGetEhsCapabilities";
import classes from "./PermissionsCard.module.scss";
import { EhsPermission, PermissionRowData } from "../../EhsRoleTypes";
import { PermissionsAction } from "../../PermissionsReducer";
import UsersService from "@velocity-ehs/user-utils-module";

const defaultFilters = {
  ...defaultPagination,
  rows: 20,
  page: 0,
  sortField: "name",
  sortOrder: SortDirection.ASC,
  filters: {
    name: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    capabilityID: {
      value: null,
      matchMode: FilterMatchMode.EQUALS,
    },
  },
};

const emDash = "—";

interface PermissionsCardProps {
  dispatch: Dispatch<PermissionsAction>;
  selected: Array<EhsPermission>;
  onRowClick: (rowData: PermissionRowData) => void;
  isLoading: boolean;
}

const PermissionsCard = ({
  dispatch,
  selected,
  onRowClick,
  isLoading,
}: PermissionsCardProps) => {
  const { t } = useTranslation();
  const [lazyParams, setLazyParams] = useState(defaultFilters);
  const { ehsPermissions, isFetching, pagination } =
    useGetEhsPermissions(lazyParams);

  const { ehsCapabilities, isFetching: isCapabilitiesFetching } =
    useGetEhsCapabilities(UsersService.customer.id);

  const [totalRecords, setTotalRecords] = useState<number>(0);

  useEffect(() => {
    pagination.totalElements && setTotalRecords(pagination.totalElements);
  }, [pagination]);

  const parsedCapabilities = ehsCapabilities?.map(
    (capability: { id: string; name: string }) => ({
      id: capability.id,
      label: capability.name,
    })
  );

  const onGlobalSearch = (event: any): void => {
    const value = event.target.value;
    setLazyParams((state: any) => {
      return {
        ...state,
        ...defaultPagination,
        filters: {
          ...state.filters,
          name: {
            value,
            matchMode: FilterMatchMode.CONTAINS,
          },
        },
      };
    });
  };

  const onCapabilityFilter = (capability: string[]) => {
    setLazyParams((state: any) => ({
      ...state,
      filters: {
        ...state.filters,
        capabilityID: { value: capability, matchMode: FilterMatchMode.EQUALS },
      },
    }));
  };

  const getStatesAvailableBody = (rowData: EhsPermission) => {
    if (rowData.availableQualifiers.length) {
      return <span>{rowData.availableQualifiers.length}</span>;
    }
    return emDash;
  };

  const getStatesSelectedBody = (rowData: EhsPermission) => {
    const permission = selected.find((perm) => rowData.id === perm.id);

    if (permission?.assignedQualifiers?.length) {
      return <span>{permission.assignedQualifiers?.length}</span>;
    }

    return emDash;
  };

  const getAssignedBody = (rowData: EhsPermission) => {
    const permission = selected.find((perm) => rowData.id === perm.id);
    const toggleName = `${rowData.name}-toggle`;

    return (
      <Toggle
        onChange={(e) => handleToggle(e, rowData)}
        checked={!!permission}
        name={toggleName}
      />
    );
  };

  const handleToggle = (event: any, rowData: EhsPermission) => {
    event.originalEvent.stopPropagation();
    event.value === false
      ? dispatch({ type: "remove", payload: rowData })
      : dispatch({ type: "add", payload: rowData });
  };

  return (
    <Card title={t("universal_permissions_title")} full>
      <Table
        data={ehsPermissions}
        params={lazyParams}
        totalRecords={totalRecords}
        onPage={setLazyParams}
        onSort={setLazyParams}
        onGlobalSearch={onGlobalSearch}
        onRowClick={onRowClick}
        isLoading={isFetching || isCapabilitiesFetching || isLoading}
        fullPage={false}
      >
        <Column
          field="name"
          header={
            <span className={classes.capitalize}>
              {t("universal_permission_name_title")}
            </span>
          }
          sortable
        />
        {AdvancedFilterColumn({
          field: "capability",
          filterField: "capabilityID",
          header: t("universal_capability_title"),
          options: parsedCapabilities,
          onApply: onCapabilityFilter,
          filterParams: lazyParams.filters,
          isLoading: isCapabilitiesFetching,
        })}
        <Column
          field="statesSelected"
          header={
            <span className={classes.capitalize}>
              {t("universal_states_selected_title")}
            </span>
          }
          body={getStatesSelectedBody}
        />
        <Column
          field="statesAvailable"
          header={
            <span className={classes.capitalize}>
              {t("universal_states_available_title")}
            </span>
          }
          body={getStatesAvailableBody}
        />
        <Column
          field="assigned"
          header={
            <span className={classes.capitalize}>
              {t("universal_assigned_title")}
            </span>
          }
          body={getAssignedBody}
        />
      </Table>
    </Card>
  );
};

export default PermissionsCard;
